import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Hero photo — lush fern leaves
const HERO_IMAGE = 'https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=800&q=80&auto=format';

export default function Login() {
  const { user, loading: authLoading, error, signIn, clearError } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    clearError();
    try {
      await signIn();
    } finally {
      setIsSigningIn(false);
    }
  };

  if (authLoading) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background"
        role="status"
        aria-live="polite"
        aria-label="Checking authentication status"
      >
        <span
          className="material-symbols-outlined text-primary animate-pulse"
          style={{ fontSize: 32 }}
          aria-hidden="true"
        >
          eco
        </span>
        <span className="text-gray-500 font-serif text-sm">Loading...</span>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return (
    <main className="flex min-h-screen flex-col bg-background">
      {/* Header label */}
      <header className="pt-8 pb-4 text-center z-10">
        <span className="text-editorial-black tracking-[0.2em] text-xs font-bold uppercase font-serif">
          Chloropal
        </span>
      </header>

      {/* Hero section with photo */}
      <div className="relative w-full h-[500px] flex-shrink-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${HERO_IMAGE}")` }}
          role="img"
          aria-label="Lush green fern leaves"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {/* Hero text */}
        <div className="absolute bottom-0 left-0 w-full p-8 pb-12">
          <h1 className="font-display text-white text-6xl leading-[0.9]">
            <span className="block italic font-normal mb-1">Chloro</span>
            <span className="block font-bold">pal</span>
          </h1>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-grow flex flex-col px-6 pt-6 pb-8 gap-6 justify-between bg-background">
        <div className="flex flex-col gap-6">
          <p className="text-gray-600 text-xl font-serif italic leading-relaxed text-center">
            Your personal plant care companion
          </p>
          <div className="editorial-divider" />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4 w-full mt-auto">
          <button
            onClick={handleSignIn}
            disabled={isSigningIn}
            className="btn-editorial group disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label={isSigningIn ? 'Signing in with Google' : 'Sign in with Google'}
          >
            <span>
              {isSigningIn ? 'Signing in...' : 'Sign in with Google'}
            </span>
            <span
              className="material-symbols-outlined group-hover:translate-x-1 transition-transform"
              style={{ fontSize: 20 }}
              aria-hidden="true"
            >
              arrow_forward
            </span>
          </button>

          {error && (
            <div
              role="alert"
              className="rounded-lg bg-danger/5 p-3 text-sm text-danger border border-danger/20"
            >
              {error}
            </div>
          )}

          <p className="text-gray-500 text-sm font-serif text-center pt-2">
            Track, identify, and care for your plants
          </p>
        </div>
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {isSigningIn && 'Signing in with Google'}
        {error && error}
      </div>
    </main>
  );
}
